import {HttpClient, HttpErrorResponse, HttpResponse} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {catchError, map, Observable, throwError} from 'rxjs';
import {Nachricht, NachrichtPayload} from '../../model/nachricht/nachricht';
import {ApiUrlService} from "./api-url.service";
import {NavigationService} from "../../../shared/utils/navigation.service";
import {LoggingService} from "../logging.service";

@Injectable({
    providedIn: 'root' // ✅ Ensures the service is available globally
})
export class NachrichtenService {

    private readonly http: HttpClient = inject(HttpClient);
    private apiUrlService = inject(ApiUrlService);
    private nav = inject(NavigationService);
    private loggingService = inject(LoggingService);

    /**
     * Liefert alle Nachrichten des angegebenen Ordners zurück
     */
    public getNachrichtenFromOrdner(ordner: string): Observable<Nachricht[]> {
        return this.http.get<Nachricht[]>(`${this.apiUrlService.getApiUrl()}/ordner/${ordner}/nachrichten`);
    }

    /**
     * Liefert eine Nachricht zum angegebenen Ordner und Id zurück
     */
    public getNachricht(ordner: string, nachrichtId: string): Observable<Nachricht> {
        return this.http
            .get<Nachricht>(`${this.apiUrlService.getApiUrl()}/ordner/${ordner}/nachrichten/${nachrichtId}`)
            .pipe(
                catchError((error: HttpErrorResponse) => {
                    if (error.status === 403 && error.error?.['fehler_id'] !== 'FFP_1009') {
                        this.loggingService.error(error);
                        this.nav.goTo('/');
                    }
                    return throwError(() => error);
                })
            );
    }


    /**
     * Sendet eine Nachricht
     */
    public sendNachricht(nachrichtPayload: NachrichtPayload): Observable<any> {
        return this.http.post<any>(`${this.apiUrlService.getApiUrl()}/ordner/senden/nachrichten`, nachrichtPayload);
    }

    /**
     * Speichert eine Nachricht als Entwurf
     */
    public saveEntwurf(nachrichtPayload: NachrichtPayload): Observable<string> {
        return this.http.post<any>(`${this.apiUrlService.getApiUrl()}/ordner/entwurf/nachrichten`, nachrichtPayload, {observe: 'response'})
            .pipe(
                map((resp: HttpResponse<any>) => {
                    const loc = resp.headers.get('Location');
                    if (!loc) {
                        throw new Error('No Location header in response');
                    }
                    return loc;
                })
            );
    }

    /**
     * Stellt eine Nachricht aus dem Ordner gelöscht wieder her
     */
    public restoreNachricht(nachrichtId: string): Observable<any> {
        return this.http.put(`${this.apiUrlService.getApiUrl()}/ordner/geloescht/nachrichten/${nachrichtId}`, {});
    }

    /**
     * Löscht eine Nachricht
     */
    public deleteNachricht(ordner: string, nachrichtenId: string): Observable<Nachricht> {
        return this.http.delete<Nachricht>(`${this.apiUrlService.getApiUrl()}/ordner/${ordner}/nachrichten/${nachrichtenId}`, {});
    }

    /**
     * Löscht eine Nachricht unwiderruflich
     */
    public deleteNachrichtIrrevocable(nachrichtenId: string): Observable<Nachricht> {
        return this.http.delete<Nachricht>(`${this.apiUrlService.getApiUrl()}/ordner/geloescht/nachrichten/${nachrichtenId}`, {});
    }
}
